import { useSearchParams } from 'react-router-dom';

import { Page } from '../../components/AppShell.jsx';
import { Tabs } from '../../components/ui.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions.js';
import LedgerTab from './LedgerTab.jsx';
import AdsTab from './AdsTab.jsx';
import PackagesTab from './PackagesTab.jsx';
import CouponsTab from './CouponsTab.jsx';
import SettingsTab from './SettingsTab.jsx';

/**
 * Credits.
 *
 * Everything on the platform is bought with credits — an ad for a month, a
 * boost on top of it, a tenant seeing who to call — so there are five
 * questions worth asking: where the credits went, what is currently being paid
 * for, what a top-up costs, what discounts it, and what each feature charges.
 * One tab each, and each is hidden from an admin whose role cannot open it
 * rather than shown and then refused by the API.
 */
export default function CreditsPage() {
  const { can } = useAuth();

  const canSeeLedger = can(PERMISSIONS.FINANCE_VIEW);
  const canSeeAds = can(PERMISSIONS.BROKER_PROPERTY_VIEW);
  const canSeePackages = can(PERMISSIONS.CONFIG_VIEW);
  const canSeeCoupons = can(PERMISSIONS.CONFIG_VIEW);
  const canSeeRates = can(PERMISSIONS.CONFIG_VIEW);

  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');

  const tabs = [
    ...(canSeeLedger ? [{ value: 'ledger', label: 'Ledger' }] : []),
    ...(canSeeAds ? [{ value: 'ads', label: 'Ads' }] : []),
    ...(canSeePackages ? [{ value: 'packages', label: 'Packages' }] : []),
    ...(canSeeCoupons ? [{ value: 'coupons', label: 'Coupons' }] : []),
    ...(canSeeRates ? [{ value: 'rates', label: 'Rates' }] : []),
  ];

  const fallback = tabs[0]?.value;
  const tab = tabs.some((entry) => entry.value === requested) ? requested : fallback;

  return (
    <Page title="Credits" subtitle="What people are spending, and what it is buying.">
      {tabs.length > 1 ? (
        <Tabs
          tabs={tabs}
          value={tab}
          label="Credit views"
          onChange={(value) => setParams(value === fallback ? {} : { tab: value })}
        />
      ) : null}

      {tab === 'rates' ? (
        <SettingsTab />
      ) : tab === 'coupons' ? (
        <CouponsTab />
      ) : tab === 'packages' ? (
        <PackagesTab />
      ) : tab === 'ads' ? (
        <AdsTab />
      ) : (
        <LedgerTab />
      )}
    </Page>
  );
}
